import React, { useState, useEffect } from 'react';
import { getPastries, createPastry, updatePastry, deletePastry, getAdminOrders, getUsers, createUser, updateUser, deleteUser, resetUserPassword, getLoginLogs } from '../services/api';
import './AdminPanel.css';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('articles');
  const [pastries, setPastries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [userFormData, setUserFormData] = useState({ username: '', password: '', role: 'vendeur', name: '' });
  const [editingUserId, setEditingUserId] = useState(null);
  const [resetPasswordId, setResetPasswordId] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ newPassword: '' });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ isOpen: false, userId: null, userName: '' });
  const [deleteConfirmPastry, setDeleteConfirmPastry] = useState({ isOpen: false, pastryId: null, pastryName: '' });

  useEffect(() => {
    if (activeTab === 'articles') {
      loadPastries();
    } else if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'logs') {
      loadLoginLogs();
    }
  }, [activeTab]);

  const loadPastries = async () => {
    setLoading(true);
    setError(null);
    const result = await getPastries();
    if (result.success) {
      setPastries(result.pastries);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    const result = await getAdminOrders();
    if (result.success) {
      setOrders(result.orders);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    const result = await getUsers();
    if (result.success) {
      setUsers(result.users);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const loadLoginLogs = async () => {
    setLoading(true);
    setError(null);
    const result = await getLoginLogs(100);
    if (result.success) {
      setLoginLogs(result.logs);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Le nom de l\'article est requis');
      return;
    }

    setLoading(true);
    let result;

    if (editingId) {
      result = await updatePastry(editingId, formData.name, formData.description);
    } else {
      result = await createPastry(formData.name, formData.description);
    }

    if (result.success) {
      setFormData({ name: '', description: '' });
      setEditingId(null);
      await loadPastries();
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleEdit = (pastry) => {
    setFormData({ name: pastry.name, description: pastry.description || '' });
    setEditingId(pastry.id);
  };

  const handleDelete = async (id) => {
    const pastry = pastries.find(p => p.id === id);
    if (pastry) {
      setDeleteConfirmPastry({ isOpen: true, pastryId: id, pastryName: pastry.name });
    }
  };

  const handleConfirmDeletePastry = async () => {
    if (!deleteConfirmPastry.pastryId) return;

    setLoading(true);
    const result = await deletePastry(deleteConfirmPastry.pastryId);
    if (result.success) {
      setDeleteConfirmPastry({ isOpen: false, pastryId: null, pastryName: '' });
      await loadPastries();
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData({ name: '', description: '' });
    setEditingId(null);
  };

  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddOrUpdateUser = async (e) => {
    e.preventDefault();
    setError(null);

    if (!userFormData.username.trim() || !userFormData.name.trim() || !userFormData.role.trim()) {
      setError('Tous les champs sont requis');
      return;
    }

    if (!editingUserId && !userFormData.password.trim()) {
      setError('Le mot de passe est requis pour un nouvel utilisateur');
      return;
    }

    setLoading(true);
    let result;

    if (editingUserId) {
      result = await updateUser(editingUserId, userFormData.role, userFormData.name);
    } else {
      result = await createUser(userFormData.username, userFormData.password, userFormData.role, userFormData.name);
    }

    if (result.success) {
      setUserFormData({ username: '', password: '', role: 'vendeur', name: '' });
      setEditingUserId(null);
      await loadUsers();
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleEditUser = (user) => {
    setUserFormData({ username: user.username, password: '', role: user.role, name: user.name });
    setEditingUserId(user.id);
  };

  const handleDeleteUser = async (id) => {
    setLoading(true);
    const result = await deleteUser(id);
    if (result.success) {
      setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' });
      await loadUsers();
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleResetPassword = async (e, userId) => {
    e.preventDefault();
    setError(null);

    if (!resetPasswordForm.newPassword.trim()) {
      setError('Le nouveau mot de passe est requis');
      return;
    }

    setLoading(true);
    const result = await resetUserPassword(userId, resetPasswordForm.newPassword);
    if (result.success) {
      setResetPasswordId(null);
      setResetPasswordForm({ newPassword: '' });
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  const handleCancelUserForm = () => {
    setUserFormData({ username: '', password: '', role: 'vendeur', name: '' });
    setEditingUserId(null);
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'articles' ? 'active' : ''}`}
            onClick={() => setActiveTab('articles')}
          >
            Articles
          </button>
          <button
            className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Commandes
          </button>
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Utilisateurs
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            Journaux de connexion
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeTab === 'articles' && (
        <div className="pastries-section">
          <div className="pastries-form">
            <h2>{editingId ? 'Modifier' : 'Ajouter'} un article</h2>
            <form onSubmit={handleAddOrUpdate}>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Croissant aux amandes"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description optionnelle"
                  rows="3"
                  disabled={loading}
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading} className="btn-primary">
                  {editingId ? 'Mettre à jour' : 'Ajouter'}
                </button>
                {editingId && (
                  <button type="button" onClick={handleCancel} disabled={loading} className="btn-secondary">
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="pastries-list">
            <h2>Liste des pâtisseries ({pastries.length})</h2>
            {loading && <p className="loading">Chargement...</p>}
            {pastries.length === 0 ? (
              <p className="empty">Aucune pâtisserie. Ajoutez-en une!</p>
            ) : (
              <div className="pastry-cards">
                {pastries.map(pastry => (
                  <div key={pastry.id} className="pastry-card">
                    <div className="pastry-content">
                      <h3>{pastry.name}</h3>
                      {pastry.description && <p>{pastry.description}</p>}
                    </div>
                    <div className="pastry-actions">
                      <button
                        onClick={() => handleEdit(pastry)}
                        className="btn-edit"
                        disabled={loading}
                      >
                        ✎ Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(pastry.id)}
                        className="btn-delete"
                        disabled={loading}
                      >
                        × Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {deleteConfirmPastry.isOpen && (
            <div className="modal-overlay" onClick={() => setDeleteConfirmPastry({ isOpen: false, pastryId: null, pastryName: '' })}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Confirmation de suppression</h2>
                <p>Êtes-vous sûr de vouloir supprimer cet article ? <strong>{deleteConfirmPastry.pastryName}</strong></p>
                <div className="modal-actions">
                  <button
                    onClick={handleConfirmDeletePastry}
                    className="btn-confirm"
                    disabled={loading}
                  >
                    Supprimer
                  </button>
                  <button
                    onClick={() => setDeleteConfirmPastry({ isOpen: false, pastryId: null, pastryName: '' })}
                    className="btn-cancel"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'orders' && (
        <div className="orders-section">
          <h2>Toutes les commandes ({orders.length})</h2>
          {loading && <p className="loading">Chargement...</p>}
          {orders.length === 0 ? (
            <p className="empty">Aucune commande.</p>
          ) : (
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Lieu de retrait</th>
                    <th>Date</th>
                    <th>Statut</th>
                    <th>Articles</th>
                    <th>Créé par</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const itemsArray = Array.isArray(order.items) ? order.items : [];
                    return (
                      <tr key={order.id}>
                        <td>{order.customerName}</td>
                        <td>{order.pickupLocation}</td>
                        <td>{new Date(order.pickupDate).toLocaleDateString('fr-FR')}</td>
                        <td className={`status-${order.status}`}>
                          {order.status === 'pending' && 'En attente'}
                          {order.status === 'inprogress' && 'En cours'}
                          {order.status === 'ready' && 'Prêt'}
                          {order.status === 'completed' && 'Terminé'}
                        </td>
                        <td>
                          {itemsArray.length > 0 ? (
                            <span title={itemsArray.map(i => `${i.name} (${i.quantity})`).join(', ')}>
                              {itemsArray.length} article{itemsArray.length > 1 ? 's' : ''}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>{order.createdBy || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="users-section">
          <div className="users-form">
            <h2>{editingUserId ? 'Modifier' : 'Ajouter'} un utilisateur</h2>
            <form onSubmit={handleAddOrUpdateUser}>
              <div className="form-group">
                <label>Nom d'utilisateur *</label>
                <input
                  type="text"
                  name="username"
                  value={userFormData.username}
                  onChange={handleUserInputChange}
                  placeholder="Ex: john_doe"
                  disabled={loading || editingUserId}
                />
              </div>

              <div className="form-group">
                <label>Mot de passe {editingUserId ? '(laisser vide pour ne pas changer)' : '*'}</label>
                <input
                  type="password"
                  name="password"
                  value={userFormData.password}
                  onChange={handleUserInputChange}
                  placeholder="Mot de passe"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  name="name"
                  value={userFormData.name}
                  onChange={handleUserInputChange}
                  placeholder="Ex: John Doe"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Rôle *</label>
                <select
                  name="role"
                  value={userFormData.role}
                  onChange={handleUserInputChange}
                  disabled={loading}
                >
                  <option value="vendeur">Vendeur</option>
                  <option value="patissier">Pâtissier</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading} className="btn-primary">
                  {editingUserId ? 'Mettre à jour' : 'Ajouter'}
                </button>
                {editingUserId && (
                  <button type="button" onClick={handleCancelUserForm} disabled={loading} className="btn-secondary">
                    Annuler
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="users-list">
            <h2>Liste des utilisateurs ({users.length})</h2>
            {loading && <p className="loading">Chargement...</p>}
            {users.length === 0 ? (
              <p className="empty">Aucun utilisateur.</p>
            ) : (
              <div className="users-table-wrapper">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Nom d'utilisateur</th>
                      <th>Nom complet</th>
                      <th>Rôle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.name}</td>
                        <td>{user.role}</td>
                        <td className="user-actions">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="btn-edit"
                            disabled={loading}
                          >
                            ✎ Modifier
                          </button>
                          <button
                            onClick={() => setDeleteConfirmModal({ isOpen: true, userId: user.id, userName: user.name })}
                            className="btn-delete"
                            disabled={loading}
                          >
                            × Supprimer
                          </button>
                          {resetPasswordId === user.id && (
                            <form onSubmit={(e) => handleResetPassword(e, user.id)} className="reset-password-form">
                              <input
                                type="password"
                                value={resetPasswordForm.newPassword}
                                onChange={(e) => setResetPasswordForm({ newPassword: e.target.value })}
                                placeholder="Nouveau MDP"
                                disabled={loading}
                                required
                              />
                              <button type="submit" disabled={loading} className="btn-confirm">
                                Confirmer
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setResetPasswordId(null);
                                  setResetPasswordForm({ newPassword: '' });
                                }}
                                disabled={loading}
                                className="btn-cancel"
                              >
                                Annuler
                              </button>
                            </form>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {deleteConfirmModal.isOpen && (
            <div className="modal-overlay" onClick={() => setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' })}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Confirmation de suppression</h2>
                <p>Êtes-vous sûr de vouloir supprimer cet utilisateur ? <strong>{deleteConfirmModal.userName}</strong></p>
                <div className="modal-actions">
                  <button
                    onClick={() => handleDeleteUser(deleteConfirmModal.userId)}
                    className="btn-confirm"
                    disabled={loading}
                  >
                    Supprimer
                  </button>
                  <button
                    onClick={() => setDeleteConfirmModal({ isOpen: false, userId: null, userName: '' })}
                    className="btn-cancel"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="logs-section">
          <h2>Journaux de connexion ({loginLogs.length})</h2>
          {loading && <p className="loading">Chargement...</p>}
          {loginLogs.length === 0 ? (
            <p className="empty">Aucun journal de connexion.</p>
          ) : (
            <div className="logs-table-wrapper">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Nom d'utilisateur</th>
                    <th>Statut</th>
                    <th>Adresse IP</th>
                    <th>User Agent</th>
                    <th>Date/Heure</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLogs.map((log, idx) => (
                    <tr key={idx} className={log.success ? 'success' : 'failed'}>
                      <td>{log.username}</td>
                      <td className={`status-${log.success ? 'success' : 'failed'}`}>
                        {log.success ? '✓ Succès' : '✗ Échec'}
                      </td>
                      <td>{log.ipAddress || 'N/A'}</td>
                      <td className="user-agent">{log.userAgent || 'N/A'}</td>
                      <td>{new Date(log.loginTime).toLocaleString('fr-FR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
